import React, { useState, useCallback } from "react";
import styles from "./ImageToWebP.module.css";

const ImageToWebP = () => {
  const [image, setImage] = useState(null);
  const [convertedImage, setConvertedImage] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [quality, setQuality] = useState(80);

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length) {
      processImage(files[0]);
    }
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      processImage(file);
    }
  };

  const processImage = (file) => {
    setError(null);

    if (!file.type.match("image.*")) {
      setError("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        setImage({
          file,
          url: event.target.result,
          name: file.name,
          type: file.type,
          size: file.size,
          dimensions: { width: img.width, height: img.height },
        });
        setConvertedImage(null);
      };
      img.onerror = () => {
        setError("Failed to load image");
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      setError("Failed to read file");
    };
    reader.readAsDataURL(file);
  };

  const convertToWebP = () => {
    if (!image) {
      setError("No image to convert");
      return;
    }

    try {
      const canvas = document.createElement("canvas");
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");

        // Create white background for transparent images
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        // Convert to WebP with quality setting
        const webPUrl = canvas.toDataURL("image/webp", quality / 100);

        // Calculate file size
        const base64String = webPUrl.split(",")[1];
        const fileSize = base64String
          ? Math.round((base64String.length * 3) / 4)
          : 0;

        setConvertedImage({
          url: webPUrl,
          name: `${image.name.split(".")[0]}.webp`,
          size: fileSize,
          dimensions: { width: img.width, height: img.height },
          quality: quality,
        });
      };
      img.onerror = () => {
        setError("Failed to process image");
      };
      img.src = image.url;
    } catch (err) {
      setError("Conversion failed: " + err.message);
    }
  };

  const downloadImage = () => {
    if (!convertedImage) {
      setError("No converted image to download");
      return;
    }

    try {
      const link = document.createElement("a");
      link.href = convertedImage.url;
      link.download = convertedImage.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError("Download failed: " + err.message);
    }
  };

  const resetAll = () => {
    setImage(null);
    setConvertedImage(null);
    setError(null);
    setQuality(80);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 bytes";
    if (bytes < 1024) return bytes + " bytes";
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    else return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Image to WebP Converter</h1>

      {/* Step Guide */}
      {!image && !convertedImage && (
        <div className={styles.stepsGuide}>
          <h2>How to Convert Images to WebP</h2>
          <div className={styles.steps}>
            <div className={styles.step}>
              <div className={styles.stepNumber}>1</div>
              <div className={styles.stepContent}>
                <h4>Upload Image</h4>
                <p>Drag & drop or click to select your image file</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>2</div>
              <div className={styles.stepContent}>
                <h4>Adjust Quality</h4>
                <p>Set the compression level for optimal file size</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>3</div>
              <div className={styles.stepContent}>
                <h4>Convert to WebP</h4>
                <p>Transform your image to modern WebP format</p>
              </div>
            </div>
            <div className={styles.step}>
              <div className={styles.stepNumber}>4</div>
              <div className={styles.stepContent}>
                <h4>Download Result</h4>
                <p>Save your optimized WebP image</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upload Section */}
      {!image && !convertedImage && (
        <div className={styles.uploadSection}>
          <div
            className={`${styles.dropArea} ${
              isDragging ? styles.dragOver : ""
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => document.getElementById("fileInput").click()}
          >
            <div className={styles.dropContent}>
              <div className={styles.uploadIcon}>🖼️</div>
              <h3 className={styles.dropTitle}>Drop your image here</h3>
              <p className={styles.dropText}>or</p>
              <input
                type="file"
                id="fileInput"
                accept="image/*"
                onChange={handleFileChange}
                className={styles.fileInput}
              />
              <button className={styles.browseButton}>Browse Files</button>
              <p className={styles.supportedFormats}>
                Supports: JPG, PNG, GIF, BMP, and more
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Comprehensive Content Section */}
      {!image && !convertedImage && (
        <div className={styles.contentSection}>
          <div className={styles.contentGrid}>
            {/* Reasons to Use WebP Format */}
            <div className={styles.contentBlock}>
              <h2>Reasons to Use WebP Format</h2>
              <p>
                WebP is an image format launched by Google which provides
                high-quality lossy and lossless compression for images on the
                web. Using WebP, webmasters and web developers can create
                smaller, richer images that help make the web faster.
              </p>

              <div className={styles.benefitsGrid}>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🚀</div>
                  <h3>Loading Speed</h3>
                  <p>
                    WebP images are 25%-35% smaller than comparable JPEG and PNG
                    images, which is noticeably faster.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>📈</div>
                  <h3>SEO</h3>
                  <p>
                    Google favors fast-loading sites and WebP will improve your
                    Core Web Vitals scores for Google's Page Experience and
                    search ranking benefits.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🎨</div>
                  <h3>Rich Features</h3>
                  <p>
                    Supports transparency like PNG, animation like GIF, and
                    superior compression like JPEG - all in a single format.
                  </p>
                </div>
                <div className={styles.benefitCard}>
                  <div className={styles.benefitIcon}>🌍</div>
                  <h3>Wide Support</h3>
                  <p>
                    Our WebP images will work in all modern web browsers,
                    including Chrome, Firefox, Safari, and Edge (97% global
                    coverage).
                  </p>
                </div>
              </div>
            </div>

            {/* Perfect Use Cases for WebP */}
            <div className={styles.contentBlock}>
              <h2>Perfect Use Cases for WebP</h2>
              <div className={styles.useCases}>
                <div className={styles.useCaseColumn}>
                  <h3>✅ Best for Use with WebP:</h3>
                  <ul>
                    <li>Website hero images and banners</li>
                    <li>E-commerce product images</li>
                    <li>Blog post featured images</li>
                    <li>Social media graphics</li>
                    <li>Mobile App images</li>
                    <li>Online advertisements</li>
                    <li>Email marketing graphics</li>
                  </ul>
                </div>
                <div className={styles.useCaseColumn}>
                  <h3>⚠️ Not Recommended Use:</h3>
                  <ul>
                    <li>Print materials for professional purposes</li>
                    <li>Medical imaging (DICOM)</li>
                    <li>Editing photographs in a RAW format</li>
                    <li>Classic systems Where required</li>
                    <li>For support of IE11</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Picking Quality */}
            <div className={styles.contentBlock}>
              <h2>Picking Quality</h2>
              <div className={styles.qualityGuide}>
                <div className={styles.qualityLevel}>
                  <h4>85% - 100% Quality</h4>
                  <p>
                    <strong>Best for use with:</strong> Professional
                    photography, product images
                    <br />
                    <strong>Great quality for:</strong> Almost lossless, minimal
                    compression
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <h4>70% - 85% Quality</h4>
                  <p>
                    <strong>Best for use with:</strong> Website images, blog
                    content
                    <br />
                    <strong>Great quality for:</strong> Best quality vs image
                    size ratio
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <h4>50% - 70% Quality</h4>
                  <p>
                    <strong>Best for use with:</strong> Thumbnails, social media
                    previews
                    <br />
                    <strong>Great quality for:</strong> Good balance of quality
                    and file size
                  </p>
                </div>
                <div className={styles.qualityLevel}>
                  <h4>Below 50% Quality</h4>
                  <p>
                    <strong>Best for use with:</strong> Extreme compression
                    needs
                    <br />
                    <strong>Great quality for:</strong> Maximum file size
                    reduction
                  </p>
                </div>
              </div>
            </div>

            {/* Frequently Asked Questions */}
            <div className={styles.contentBlock}>
              <h2>Frequently Asked Questions</h2>
              <div className={styles.faqGrid}>
                <div className={styles.faqItem}>
                  <h4>Is WebP better than JPEG and PNG?</h4>
                  <p>
                    Yes, WebP will generally compress the file size 25-35%
                    smaller than equivalent JPEG and PNG files at the same
                    quality, which makes it the better image standard for the
                    web when trying to maintain its visual quality.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Does WebP work in all browsers?</h4>
                  <p>
                    Most modern browsers support WebP, including Chrome,
                    Firefox, Edge, and Safari. For older browsers (e.g., IE11),
                    you will need the ability to use the picture element with
                    fallback images.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Is there quality loss when converting to WebP?</h4>
                  <p>
                    With proper quality settings (75-85%) the quality loss is
                    very small to unnoticeable. Again, WebP uses an advanced
                    compression technique that maintains visual quality much
                    better than JPEG or PNG at the same end file size.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Can I convert WebP back to JPEG or other formats?</h4>
                  <p>
                    Yes, you can use our other conversion tools to convert WebP
                    back to PNG or JPEG if there is a need for the image in
                    another application or a deprecated system.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>What is the largest file size that I can convert?</h4>
                  <p>
                    Our tool can take images up to 50MB. For larger files, we
                    recommend to first resize the image, or to use desktop
                    software to complete the conversion.
                  </p>
                </div>
                <div className={styles.faqItem}>
                  <h4>Is WebP good for printing?</h4>
                  <p>
                    WebP is an image format optimized for digital display. If
                    you are looking to print professionally, you will want to
                    print in TIFF format or high-quality JPEG, which are
                    generally accepted as industry printing standard for print
                    production.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Editor Section */}
      {(image || convertedImage) && (
        <div className={styles.editorSection}>
          <div className={styles.mainContent}>
            {/* Editor Side */}
            <div className={styles.editorSide}>
              <div className={styles.sectionHeader}>
                <h3>Original Image</h3>
                <button onClick={resetAll} className={styles.changeImageButton}>
                  Change Image
                </button>
              </div>

              {image && (
                <>
                  <div className={styles.cropSection}>
                    <img
                      src={image.url}
                      alt="Original"
                      className={styles.cropImage}
                    />
                  </div>

                  <div className={styles.controls}>
                    <div className={styles.qualityControl}>
                      <label htmlFor="qualitySlider">
                        Image Quality: {quality}%
                      </label>
                      <input
                        type="range"
                        id="qualitySlider"
                        min="1"
                        max="100"
                        value={quality}
                        onChange={(e) => setQuality(parseInt(e.target.value))}
                        className={styles.qualitySlider}
                      />
                    </div>

                    <div className={styles.helpText}>
                      WebP provides superior compression with both lossy and
                      lossless compression
                    </div>

                    <div className={styles.buttonGroup}>
                      <button
                        onClick={convertToWebP}
                        className={styles.processButton}
                      >
                        Convert to WebP
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Preview Side */}
            {convertedImage && (
              <div className={styles.previewSide}>
                <div className={styles.previewSection}>
                  <h3>Converted WebP Image</h3>
                  <div className={styles.previewContent}>
                    <div className={styles.previewImageContainer}>
                      <img
                        src={convertedImage.url}
                        alt="Converted WebP"
                        className={styles.previewImage}
                      />
                    </div>

                    <div className={styles.imageInfo}>
                      <h4>File Information</h4>
                      <div className={styles.detailItem}>
                        <span>Format:</span>
                        <strong>WebP</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Quality:</span>
                        <strong>{convertedImage.quality}%</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Compression:</span>
                        <strong>Lossy</strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>Dimensions:</span>
                        <strong>
                          {convertedImage.dimensions.width} ×{" "}
                          {convertedImage.dimensions.height}
                        </strong>
                      </div>
                      <div className={styles.detailItem}>
                        <span>File Size:</span>
                        <strong>{formatFileSize(convertedImage.size)}</strong>
                      </div>
                    </div>

                    <button
                      onClick={downloadImage}
                      className={styles.downloadButton}
                    >
                      Download WebP
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageToWebP;
